import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';

import reservationsAdapter, { IReservation } from '../adapters/reservationsAdapter';
import { IEmployee } from '../adapters/employeesAdapter';
import { IPropertyMgmtOrg } from '../adapters/propertyMgmtOrgsAdapter';
import useTranslator from '../../../utilities/hooks/useTranslator';
import useLocaleFormater from '../../../utilities/hooks/useLocaleFormater';
import { parseApiDate } from '../../../utilities/apiDates';
import LoadingState from '../../../components/LoadingState';
import { isApiConcurrencyError } from '../../../utilities/IApiConcurrencyError';
import useApiClient from '../../../utilities/hooks/useApiClient';

export interface IStopDialogProps {
    openInfo: { open: boolean, reservation: IReservation } | undefined,
    setOpenInfo: (openInfo: { open: boolean, reservation: IReservation } | undefined) => void,
    allEmployees: IEmployee[],
    propertyMgmtOrg: IPropertyMgmtOrg,
    safeSetViewLoadingState: (loadingState: LoadingState) => void,
    refreshSingleReservationInList: (reservation: IReservation) => void,
    safeSetUserMessageContent: (messageContent: string) => void
}

const StopDialogDict = {
    stopDlgTitle: { en: 'Stop', fr: 'Arrêt' },
    stopDlgTextBeforeJobName: { en: 'Stop employee on job ', fr: 'Arrêt pour l\'employé sur la job ' },
    stopDlgTextAfterJobName: { en: ' starting on {0} and that must be done before {1}.', fr: ' commençant le {0} et qui doit être terminée avant le {1}.' },
    stopDlgOkActionLbl: { en: 'Ok', fr: 'Ok' },
    stopDlgCancelActionLbl: { en: 'Cancel', fr: 'Annuler' },
    stopConcurrencyError: { en: 'Reservation for {0} has been modified by another user. Job cannot be stopped for employee. Please refresh view and retry if needed.', fr: 'La réservation pour {0} a été modifiée par un autre utilisateurs. La job ne peut être arrêtée pour l\'employé. SVP rafraîchir et réessayer si nécessaire.' },
    stopInternalError: { en: 'Internal error while trying to stop job of {0} for {1}. Please refresh and retry if needed.', fr: 'Erreur interne lors de l\'arrêt de la job pour {0} pour {1}. SVP rafraîchir et réessayer si nécessaire.' }
};

const StopDialog = (props: IStopDialogProps) => {

    const translate = useTranslator();
    const formater = useLocaleFormater();
    const apiClient = useApiClient();

    const [dialogEmployeeIdToStop, setDialogEmployeeIdToStop] = useState<string>('');

    const cancelStartDialog = () => {

        setDialogEmployeeIdToStop('');
        props.setOpenInfo({ open: false, reservation: props.openInfo!.reservation });
    }

    const getFormattedJobDate = (jobDate: string) => formater.formatDate(parseApiDate(jobDate), { month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' });

    const getEmployeesToStart = (reservation: IReservation) => reservation.cleaningJob.employees.filter((employee) => employee.status === 'Working');

    const getEmployee = (id: string) => props.allEmployees?.find((employee) => employee.id === id);

    const getFormattedEmployeeName = (employee: IEmployee) => employee.lastName + ' ' + employee.firstName;

    const getFormattedEmployeeNameWithId = (id: string) => {

        const employee = getEmployee(id);
        return employee ? getFormattedEmployeeName(employee) : '';
    };

    const stopEmployee = () => {

        const employeeToStart = getEmployee(dialogEmployeeIdToStop);

        if (!employeeToStart)
            throw new Error('Internal error : Employee cannot be found.');

        const reservationToStart = props.openInfo!.reservation;

        setDialogEmployeeIdToStop('');
        props.setOpenInfo({ open: false, reservation: props.openInfo!.reservation });

        props.safeSetViewLoadingState(LoadingState.ModalWithVisibleContent);

        reservationsAdapter.stopEmployeeOnCleaningJob(
            apiClient,
            props.propertyMgmtOrg?.id,
            reservationToStart.id,
            reservationToStart.aggregateTimeStamp,
            { targetEmployeeUniqueUserId: employeeToStart.uniqueUserId },
            true
        )
            .then((value) => {
                props.refreshSingleReservationInList(value)
            })
            .catch((error: any) => {

                if (isApiConcurrencyError(error))
                    props.safeSetUserMessageContent(translate(StopDialogDict.stopConcurrencyError, [reservationToStart.rentalUnit.name]));
                else
                    props.safeSetUserMessageContent(translate(StopDialogDict.stopInternalError, [reservationToStart.rentalUnit.name, getFormattedEmployeeName(employeeToStart)]));
            })
            .finally(() => props.safeSetViewLoadingState(LoadingState.None));
    };

    return <Dialog fullWidth onClose={() => { }} open={!!props.openInfo && props.openInfo.open} keepMounted data-cy='StopDialog'>
        <DialogTitle>{translate(StopDialogDict.stopDlgTitle)}</DialogTitle>
        <DialogContent dividers>
            <DialogContentText data-cy='StopDialogText'>{translate(StopDialogDict.stopDlgTextBeforeJobName)}<Box component='span' sx={{ fontWeight: 700 }}>{props.openInfo?.reservation.rentalUnit.name}</Box>{translate(StopDialogDict.stopDlgTextAfterJobName, [props.openInfo ? getFormattedJobDate(props.openInfo.reservation.endDate) : '', props.openInfo ? getFormattedJobDate(props.openInfo.reservation.cleaningJob.mustBeCompletedBeforeDate) : ''])}</DialogContentText>
            <FormControl sx={{ mt: 2 }}>
                <RadioGroup
                    aria-labelledby="employees-to-start"
                    name="employees-to-start-rd"
                    onChange={(ev, value) => setDialogEmployeeIdToStop(value)}
                    value={dialogEmployeeIdToStop}>
                    {props.openInfo && getEmployeesToStart(props.openInfo.reservation).map((cleaningEmployee, index) => <FormControlLabel value={cleaningEmployee.id} key={cleaningEmployee.id} control={<Radio />} label={getFormattedEmployeeNameWithId(cleaningEmployee.id)} data-cy={'StopDialogEmployee_' + index.toString()} />)}
                </RadioGroup>
            </FormControl>
        </DialogContent>
        <DialogActions>
            <Button autoFocus onClick={cancelStartDialog} data-cy='StopDialogCancelAction'>{translate(StopDialogDict.stopDlgCancelActionLbl)}</Button>
            <Button disabled={dialogEmployeeIdToStop === ''} onClick={stopEmployee} data-cy='StopDialogOkAction'>{translate(StopDialogDict.stopDlgOkActionLbl)}</Button>
        </DialogActions>
    </Dialog>;
};

export default StopDialog